import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useState, useMemo } from 'react';
import { Box, Typography, Button, Alert, TextField } from '@weave-mui/material';
import PermissionButton from '../../components/PermissionButton';
import { Actions, usePermissions } from '../../hooks/usePermissions';
import FailedCollectionsModal from './FailedCollectionsModal';
import { CircularProgress, Checkbox, FormControlLabel, FormGroup, Grid } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../api/apiClient';
import { queryKeys } from '../../query/queryClient';
function parseCollectionIds(raw) {
    return raw
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
}
function parseCsvCollectionIds(csvText) {
    const lines = csvText
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
    if (lines.length === 0)
        return [];
    const header = lines[0]
        .replace(/^\uFEFF/, '')
        .split(',')
        .map((h) => h.trim().toLowerCase());
    const colIdx = header.indexOf('collectionid');
    if (colIdx === -1) {
        return lines
            .slice(1)
            .map((l) => l.split(',')[0].trim())
            .filter(Boolean);
    }
    return lines
        .slice(1)
        .map((line) => (line.split(',')[colIdx] ?? '').trim())
        .filter(Boolean);
}
const CreateJobTab = () => {
    const { canWrite } = usePermissions();
    const fileInputRef = useRef(null);
    const [batchId, setBatchId] = useState('');
    const [collectionsText, setCollectionsText] = useState('');
    const [selectedTaskNames, setSelectedTaskNames] = useState({});
    const [snapshotTime, setSnapshotTime] = useState(() => new Date().toISOString().split('T')[0]);
    const [skipDbScan, setSkipDbScan] = useState(true);
    const [isDryRun, setDryRun] = useState(false);
    const [forceRescan, setForceRescan] = useState(false);
    const [skipCollectionInputFilter, setSkipCollectionInputFilter] = useState(false);
    const [skipScanResultUpload, setSkipScanResultUpload] = useState(false);
    const [errors, setErrors] = useState({});
    const [successJobId, setSuccessJobId] = useState(null);
    const [successWarning, setSuccessWarning] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const parsedCollectionIds = useMemo(() => parseCollectionIds(collectionsText), [collectionsText]);
    const { data: refreshTimeData, isLoading: isLoadingRefreshTime } = useQuery({
        queryKey: queryKeys.dataFix.dbRefreshTime(),
        queryFn: api.dataFix.getDbRefreshTime,
    });
    const fetchedSnapshotTime = refreshTimeData?.dbRefreshTime ?? null;
    const isSnapshotReadOnly = !!fetchedSnapshotTime;
    const snapshotTimeValue = fetchedSnapshotTime ?? snapshotTime;
    const { data: casesData, isLoading: isLoadingCases, isError: isCasesError, } = useQuery({
        queryKey: queryKeys.dataFix.cases(),
        queryFn: () => api.dataFix.getCases(),
    });
    const cases = casesData?.cases ?? [];
    const createJobMutation = useMutation({
        mutationFn: (payload) => api.dataFix.createJob(payload),
        onSuccess: (data) => {
            setSuccessJobId(data.jobId);
            setSuccessWarning(data.warning ?? null);
            setSubmitError(null);
            setBatchId('');
            setCollectionsText('');
            setSelectedTaskNames({});
            setSnapshotTime(new Date().toISOString().split('T')[0]);
            setSkipDbScan(true);
            setDryRun(false);
            setForceRescan(false);
            setSkipCollectionInputFilter(false);
            setSkipScanResultUpload(false);
            setErrors({});
        },
        onError: (err) => {
            setSubmitError(err?.message ?? 'Failed to create job');
            setSuccessJobId(null);
            setSuccessWarning(null);
        },
    });
    const allSelected = cases.length > 0 && cases.every((c) => selectedTaskNames[c.taskName]);
    const someSelected = cases.some((c) => selectedTaskNames[c.taskName]);
    const isIndeterminate = someSelected && !allSelected;
    const selectedCases = cases.filter((c) => selectedTaskNames[c.taskName]);
    const allTaskNamesAllowEmptyCollections = selectedCases.length > 0 && selectedCases.every((c) => c.enableNoCollectionListInput);
    const allowEmptyCollections = allTaskNamesAllowEmptyCollections;
    const isViolatingEmptyCollectionRule = !allowEmptyCollections && parsedCollectionIds.length === 0;
    const skipCollectionInputFilterActive = skipCollectionInputFilter && allTaskNamesAllowEmptyCollections;
    const handleSkipCollectionInputFilterChange = (checked) => {
        setSkipCollectionInputFilter(checked);
        if (checked)
            setCollectionsText('');
    };
    function handleSelectAll(checked) {
        setSelectedTaskNames(Object.fromEntries(cases.map((c) => [c.taskName, checked])));
    }
    function handleCaseToggle(taskName, checked) {
        setSelectedTaskNames((prev) => ({ ...prev, [taskName]: checked }));
    }
    function handleModalConfirm(selectedIds, allModalIds) {
        setCollectionsText((prev) => {
            const existing = parseCollectionIds(prev);
            const modalIdSet = new Set(allModalIds);
            const selectedSet = new Set(selectedIds);
            const kept = existing.filter((id) => !modalIdSet.has(id) || selectedSet.has(id));
            const keptSet = new Set(kept);
            const added = selectedIds.filter((id) => !keptSet.has(id));
            return [...kept, ...added].join('\n');
        });
        setModalOpen(false);
    }
    function handleUpload(e) {
        const file = e.target.files?.[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const csvText = ev.target?.result;
            const ids = parseCsvCollectionIds(csvText);
            setCollectionsText(ids.join('\n'));
        };
        reader.readAsText(file);
        e.target.value = '';
    }
    function validate() {
        const errs = {};
        if (!batchId.trim())
            errs.batchId = 'Batch ID is required';
        else if (!/^[A-Za-z0-9-]+$/.test(batchId.trim()))
            errs.batchId = 'Batch ID may only contain letters, numbers, and hyphens';
        const collectionIds = parsedCollectionIds;
        if (!allowEmptyCollections && collectionIds.length === 0)
            errs.collections = 'At least one collection ID is required';
        if (!cases.some((c) => selectedTaskNames[c.taskName]))
            errs.cases = 'Select at least one case';
        if (!isSnapshotReadOnly) {
            if (!snapshotTimeValue.trim()) {
                errs.snapshotTime = 'Snapshot time is required';
            }
            else if (!/^\d{4}-\d{2}-\d{2}$/.test(snapshotTimeValue)) {
                errs.snapshotTime = 'Snapshot time must be in YYYY-MM-DD format with 2-digit month and day';
            }
            else {
                const [year, month, day] = snapshotTimeValue.split('-').map(Number);
                const d = new Date(year, month - 1, day);
                if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day)
                    errs.snapshotTime = 'Snapshot time must be a valid calendar date';
            }
        }
        return errs;
    }
    function handleSubmit() {
        if (!canWrite)
            return;
        if (isLoadingCases || isCasesError)
            return;
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        setErrors({});
        createJobMutation.mutate({
            batchId: batchId.trim(),
            collectionIds: parsedCollectionIds,
            taskNames: cases.filter((c) => selectedTaskNames[c.taskName]).map((c) => c.taskName),
            snapshotTime: snapshotTimeValue,
            options: {
                skipDbScan,
                isDryRun,
                forceRescan,
                skipCollectionInputFilter: skipCollectionInputFilterActive,
                skipScanResultUpload,
            },
        });
    }
    return (_jsxs(Box, { sx: { p: 3, maxWidth: 800 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 4, mb: 3 }, children: [_jsx(Typography, { sx: { minWidth: 160, fontWeight: 500 }, children: "DB Refresh Time" }), isLoadingRefreshTime ? (_jsx(CircularProgress, { size: 16 })) : (_jsxs(Box, { children: [_jsx(TextField, { value: snapshotTimeValue, onChange: (e) => setSnapshotTime(e.target.value), inputProps: { readOnly: isSnapshotReadOnly }, size: "small", sx: { width: 180, ...(isSnapshotReadOnly && { pointerEvents: 'none' }) } }), errors.snapshotTime && (_jsx(Alert, { severity: "error", sx: { mt: 1 }, children: errors.snapshotTime }))] }))] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 4, mb: 3 }, children: [_jsx(Typography, { sx: { minWidth: 160, fontWeight: 500, pt: 1 }, children: "Batch ID" }), _jsxs(Box, { sx: { flexGrow: 1 }, children: [_jsx(TextField, { value: batchId, onChange: (e) => setBatchId(e.target.value), placeholder: "Enter batch ID...", size: "small", fullWidth: true }), errors.batchId && (_jsx(Alert, { severity: "error", sx: { mt: 1 }, children: errors.batchId }))] })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 4, mb: 3 }, children: [_jsx(Typography, { sx: { minWidth: 160, fontWeight: 500, pt: 1 }, children: "Collections List" }), _jsxs(Box, { sx: { flexGrow: 1 }, children: [_jsxs(Box, { sx: { display: 'flex', gap: 1, mb: 1 }, children: [_jsx(Button, { variant: "outlined", size: "small", onClick: () => fileInputRef.current?.click(), disabled: skipCollectionInputFilterActive, children: "Upload..." }), _jsx("input", { ref: fileInputRef, type: "file", accept: ".csv", style: { display: 'none' }, onChange: handleUpload }), _jsx(Button, { variant: "outlined", size: "small", onClick: () => setModalOpen(true), disabled: skipCollectionInputFilterActive, children: "Add from Failed Collections" })] }), _jsx(TextField, { multiline: true, rows: 8, fullWidth: true, value: skipCollectionInputFilterActive ? '' : collectionsText, onChange: (e) => setCollectionsText(e.target.value), placeholder: "Enter collection IDs, one per line or comma-separated...", disabled: skipCollectionInputFilterActive }), !skipCollectionInputFilterActive && parsedCollectionIds.length > 0 && (_jsxs(Typography, { variant: "caption", sx: { mt: 0.5, display: 'block', color: 'text.secondary' }, children: [parsedCollectionIds.length.toLocaleString(), " collections"] })), errors.collections && (_jsx(Alert, { severity: "error", sx: { mt: 1 }, children: errors.collections }))] })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 4, mb: 4 }, children: [_jsx(Typography, { sx: { minWidth: 160, fontWeight: 500, pt: 1 }, children: "Cases" }), _jsxs(Box, { children: [errors.cases && (_jsx(Alert, { severity: "error", sx: { mb: 1 }, children: errors.cases })), isLoadingCases ? (_jsx(CircularProgress, { size: 16 })) : isCasesError ? (_jsx(Typography, { color: "error", children: "Failed to load" })) : (_jsxs(_Fragment, { children: [_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: allSelected, indeterminate: isIndeterminate, onChange: (e) => handleSelectAll(e.target.checked) }), label: "Select All" }), _jsx(FormGroup, { children: _jsx(Grid, { container: true, children: cases.map((c) => (_jsx(Grid, { item: true, xs: 6, children: _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: !!selectedTaskNames[c.taskName], onChange: (e) => handleCaseToggle(c.taskName, e.target.checked) }), label: c.displayName }) }, c.taskName))) }) })] }))] })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 4, mb: 4 }, children: [_jsx(Typography, { sx: { minWidth: 160, fontWeight: 500, pt: 1 }, children: "Options" }), _jsxs(FormGroup, { children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'row', gap: 2 }, children: [_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: skipDbScan, onChange: (e) => {
                                                const checked = e.target.checked;
                                                setSkipDbScan(checked);
                                                if (checked)
                                                    setForceRescan(false);
                                            }, disabled: forceRescan }), label: "Skip DB Scan" }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: forceRescan, onChange: (e) => {
                                                const checked = e.target.checked;
                                                setForceRescan(checked);
                                                if (checked)
                                                    setSkipDbScan(false);
                                            }, disabled: skipDbScan }), label: "Force Rescan" }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: skipScanResultUpload, onChange: (e) => setSkipScanResultUpload(e.target.checked) }), label: "Skip Scan Result Upload" }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: skipCollectionInputFilterActive, onChange: (e) => handleSkipCollectionInputFilterChange(e.target.checked), disabled: !allTaskNamesAllowEmptyCollections }), label: "Skip Collection Input Filter" })] }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: isDryRun, onChange: (e) => setDryRun(e.target.checked) }), label: "Dry Run" })] })] }), successJobId && (_jsxs(Alert, { severity: "success", sx: { mb: 2 }, children: ["Job created successfully. Job ID: ", successJobId] })), successWarning && (_jsx(Alert, { severity: "warning", sx: { mb: 2 }, children: successWarning })), submitError && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, children: submitError })), _jsx(PermissionButton, { variant: "contained", requiredAction: Actions.WRITE, onClick: handleSubmit, disabled: createJobMutation.isPending ||
                    isLoadingCases ||
                    isCasesError ||
                    isViolatingEmptyCollectionRule, children: createJobMutation.isPending ? 'Submitting...' : 'Submit' }), _jsx(FailedCollectionsModal, { open: modalOpen, onClose: () => setModalOpen(false), onConfirm: handleModalConfirm, preSelected: parseCollectionIds(collectionsText) })] }));
};
export default CreateJobTab;
